interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Decodes a Google polyline string into an array of latitude/longitude coordinates
 *
 * @param polyline - The encoded polyline string
 * @param precision - The precision factor (default: 5 for standard polylines, 6 for high
 *   precision)
 * @returns Array of LatLng coordinates
 */
export function decodePolyline(polyline: string, precision: number = 5): LatLng[] {
  const coordinates: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  const factor = Math.pow(10, precision);

  while (index < polyline.length) {
    // Decode latitude
    let result = 0;
    let shift = 0;
    let byte: number;

    do {
      byte = polyline.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += deltaLat;

    // Decode longitude
    result = 0;
    shift = 0;

    do {
      byte = polyline.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += deltaLng;

    coordinates.push({
      lat: lat / factor,
      lng: lng / factor
    });
  }

  return coordinates;
}

/**
 * Encodes an array of coordinates into a Google polyline string
 *
 * @param coordinates - Array of LatLng coordinates
 * @param precision - The precision factor (default: 5)
 * @returns Encoded polyline string
 */
export function encodePolyline(coordinates: LatLng[], precision: number = 5): string {
  const factor = Math.pow(10, precision);
  let prevLat = 0;
  let prevLng = 0;
  let polyline = '';

  for (const coord of coordinates) {
    const lat = Math.round(coord.lat * factor);
    const lng = Math.round(coord.lng * factor);

    const deltaLat = lat - prevLat;
    const deltaLng = lng - prevLng;

    polyline += encodeSignedNumber(deltaLat);
    polyline += encodeSignedNumber(deltaLng);

    prevLat = lat;
    prevLng = lng;
  }

  return polyline;
}

/** Helper function to encode a signed number for polyline encoding */
function encodeSignedNumber(num: number): string {
  let encoded = '';
  let value = num < 0 ? ~(num << 1) : num << 1;

  while (value >= 0x20) {
    encoded += String.fromCharCode((0x20 | (value & 0x1f)) + 63);
    value >>= 5;
  }

  encoded += String.fromCharCode(value + 63);
  return encoded;
}

// // Example usage with your polyline
// const yourPolyline = "*wrpGbsnBC|B@Th@@J@tABLBN@b@@h@BJ?Z@pADP?`@Bf@?B@F?L@D?lAFJ?`ABD?F@fABJ@^?j@@ALX`O@D@D@JR|DB`@@N@V?BADQ`@g@lAGLUh@CHCJCH?FAFAFi@xDAJCLE`@iA~HAH?BA@?F~B`ANFXLD@hAd@~Ap@FBHDAFABAF[vAWfACHGHEHGJIJIHGFCDCDCH*@vAU~@IXIPKNmApAKJWVsAtAKJGDGBOHUFQJQJC?MJUNa@XSPEDA@IDKFOFSDAMGGoAy@USk@a@a@KKCIAu@SSIIEcAi@KEKEg@W??o@DK@MFKJiAfA";

// const decodedCoordinates = decodePolyline(yourPolyline);

// // Log the results
// console.log("Decoded coordinates:");
// console.log(`Total points: ${decodedCoordinates.length}`);
// console.log("First 5 points:", decodedCoordinates.slice(0, 5));
// console.log("Last 5 points:", decodedCoordinates.slice(-5));

// // Optional: Re-encode to verify the decoder works correctly
// const reencoded = encodePolyline(decodedCoordinates);
// console.log("\nOriginal polyline length:", yourPolyline.length);
// console.log("Re-encoded polyline length:", reencoded.length);
// console.log("Polylines match:", yourPolyline === reencoded);

// export { decodePolyline, encodePolyline, LatLng };
